/**
 * Formats a date string from backend (with time/zone) to 'YYYY-MM-DD' for input type="date" fields
 * @param {string} dateStr
 * @returns {string}
 */
export function formatDateForInput(dateStr) {
  if (!dateStr) return ""
  const match = dateStr.match(/^(\d{4}-\d{2}-\d{2})/)
  return match ? match[1] : dateStr
}
/**
 * Formats a date to YYYY-MM-DD format
 * @param {string} dateStr - Date as string
 * @returns {string} - Date formatted as YYYY-MM-DD
 */
export function formatDate(dateStr) {
  if (!dateStr) return ""
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr
  if (dateStr.includes("T")) return dateStr.split("T")[0]
  const parts = dateStr.split("-")
  if (parts.length === 3) {
    const [y, m, d] = parts
    return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`
  }
  return dateStr
}

/**
 * Generates an invoice number based on prefixes and base number
 * @param {string} prefix1 - First prefix
 * @param {string} prefix2 - Second prefix
 * @param {string|number} baseNumber - Invoice base number
 * @returns {string} - Formatted invoice number
 */
export function buildInvoiceNumber(prefix1, prefix2, baseNumber) {
  const prefixA = (prefix1 ?? "").trim()
  const prefixB = (prefix2 ?? "").trim()
  const base = String(baseNumber)

  return (prefixA ? `${prefixA}-` : "") + (prefixB ? `${prefixB}-` : "") + base
}

export function isGroupedCertificationConcept(concept) {
  return (
    Array.isArray(concept?.certsAssociated) &&
    concept.certsAssociated.length > 1
  )
}

const CORRECTIVE_TYPES = new Set(["corrective", "credit-note", "debit-note"])

/** Mirrors the backend Pydantic constraint `max_length=500` on description / notes. */
export const INVOICE_TEXT_MAX_LENGTH = 500

/** Longest IBAN allowed by ISO 13616 (Malta/Russia are 33-34; Spain is 24). */
export const IBAN_MAX_LENGTH = 34

export const normalizeIban = value =>
  String(value || "")
    .toUpperCase()
    // \s misses the non-breaking space, which is what arrives when an IBAN is
    // pasted out of a PDF: it looks right on screen but fails validation.
    .replace(/[\s\u00a0-]/g, "")
    .slice(0, IBAN_MAX_LENGTH)

/** Per-country IBAN lengths from the ISO 13616 registry. */
const IBAN_LENGTHS = {
  AD: 24,
  AT: 20,
  BE: 16,
  BG: 22,
  CH: 21,
  CY: 28,
  CZ: 24,
  DE: 22,
  DK: 18,
  EE: 20,
  ES: 24,
  FI: 18,
  FR: 27,
  GB: 22,
  GI: 23,
  GR: 27,
  HR: 21,
  HU: 28,
  IE: 22,
  IS: 26,
  IT: 27,
  LI: 21,
  LT: 20,
  LU: 20,
  LV: 21,
  MC: 27,
  MT: 31,
  NL: 18,
  NO: 15,
  PL: 28,
  PT: 25,
  RO: 24,
  SE: 24,
  SI: 19,
  SK: 24,
  SM: 27,
}

/**
 * ISO 13616 validation, mirroring the backend's `utils/iban.py`.
 *
 * The mod-97 check digit is the point: it catches a single altered character and
 * transpositions, which a length/shape regex accepts. An unlisted country is
 * accepted on mod-97 alone rather than rejected.
 *
 * @param {string} value
 * @returns {boolean} false for empty input
 */
export const isValidIban = value => {
  const iban = normalizeIban(value)

  if (iban.length < 15 || iban.length > IBAN_MAX_LENGTH) return false
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]+$/.test(iban)) return false

  const expected = IBAN_LENGTHS[iban.slice(0, 2)]
  if (expected !== undefined && iban.length !== expected) return false

  // Move the first four characters to the end, map letters to their position value
  // (A=10..Z=35), then the number must be 1 mod 97. Reduced digit by digit because
  // the full value overflows Number.
  const rearranged = iban.slice(4) + iban.slice(0, 4)
  let remainder = 0
  for (const char of rearranged) {
    for (const digit of parseInt(char, 36).toString()) {
      remainder = (remainder * 10 + Number(digit)) % 97
    }
  }
  return remainder === 1
}

export const CREDITOR_ID_MAX_LENGTH = 35
export const MANDATE_REF_MAX_LENGTH = 35

export const normalizeCreditorId = value =>
  String(value || "")
    .toUpperCase()
    .replace(/[\s\u00a0-]/g, "")
    .slice(0, CREDITOR_ID_MAX_LENGTH)

/**
 * SEPA Creditor Identifier per EPC262-08, mirroring `utils/iban.py`.
 *
 * Layout is country code, two check digits, a 3-character Creditor Business Code,
 * then the national identifier. Unlike an IBAN the check digits cover the national
 * identifier ONLY -- the Business Code is excluded, so ZZZ and 001 must yield the
 * same digits. Computing over the whole string, the obvious mistake when copying the
 * IBAN version, rejects valid identifiers.
 *
 * @param {string} value
 * @returns {boolean} false for empty input
 */
export const isValidCreditorId = value => {
  const ci = normalizeCreditorId(value)

  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{3}[A-Z0-9]+$/.test(ci)) return false

  const payload = `${ci.slice(7)}${ci.slice(0, 2)}00`
  let remainder = 0
  for (const char of payload) {
    for (const digit of parseInt(char, 36).toString()) {
      remainder = (remainder * 10 + Number(digit)) % 97
    }
  }
  return String(98 - remainder).padStart(2, "0") === ci.slice(2, 4)
}

/** Only the outer whitespace goes: inner spaces and punctuation are part of the
 * reference, and the EPC compares it case-insensitively, so case is left alone. */
export const normalizeMandateRef = value =>
  String(value || "")
    .trim()
    .slice(0, MANDATE_REF_MAX_LENGTH)

/**
 * Length and character set only: a mandate reference carries no check digit. The
 * creditor picks it freely and the EPC constrains nothing beyond 35 characters of the
 * restricted Latin set, so anything stricter would reject legitimate mandates.
 *
 * @param {string} value
 * @returns {boolean} false for empty input
 */
export const isValidMandateRef = value => {
  const ref = normalizeMandateRef(value)
  if (!ref || ref.length > MANDATE_REF_MAX_LENGTH) return false
  return /^[A-Za-z0-9/\-?:().,'+ ]+$/.test(ref)
}

/**
 * Adapts invoice object for backend (formats dates, cleans concepts, etc).
 * @param {Object} invoice
 * @param {Object} [options] - Optional overrides applied to the adapted result (e.g. { invoiceSerie })
 * @returns {Object}
 */
export function adaptInvoiceForBackend(invoice, options = {}) {
  const company = invoice.contact.split("#")[0]

  const concepts = (invoice.invoiceConcepts || [])
    .slice()
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
    .map((concept, idx) => {
      const {
        description,
        quantity,
        price,
        taxes,
        retentions,
        PK,
        SK,
        partidaId,
        discount,
        certificationParts,
        certsAssociated,
      } = concept
      const cleanTaxes = Array.isArray(taxes)
        ? taxes.map(tax => {
            const { longName, name, type, value, cat, rate, PK, SK, active } =
              tax
            const t = { longName, name, type, value, cat, rate }
            if (PK) t.PK = PK
            if (SK) t.SK = SK
            if (typeof active !== "undefined") t.active = active
            return t
          })
        : []
      const cleanRetentions = Array.isArray(retentions)
        ? retentions.map(retention => {
            const { longName, name, type, value, cat, rate, PK, SK, active } =
              retention
            const r = { longName, name, type, value, cat, rate }
            if (PK) r.PK = PK
            if (SK) r.SK = SK
            if (typeof active !== "undefined") r.active = active
            return r
          })
        : []
      const rawCertsAssociated = certsAssociated ?? certificationParts
      const cleanCertificationParts = Array.isArray(rawCertsAssociated)
        ? rawCertsAssociated
            .map(certificationPart => {
              if (!certificationPart || typeof certificationPart !== "object") {
                return null
              }

              const { certId, partidaId, amount, projectId } = certificationPart
              if (!certId || !partidaId) return null

              return {
                projectId: projectId ?? null,
                certId,
                partidaId,
                amount: Number.isFinite(Number(amount)) ? Number(amount) : 0,
              }
            })
            .filter(Boolean)
        : []
      const safeQuantity = (() => {
        const q = Number(quantity)
        return Number.isFinite(q) && q >= 1 ? q : 1
      })()
      const safePrice = Number.isFinite(Number(price)) ? Number(price) : 0
      const safeDiscount = Number.isFinite(Number(discount))
        ? Number(discount)
        : 0

      const subtotal = getConceptSubtotal(safeQuantity, safePrice)
      const combinedTaxes = [...cleanTaxes, ...cleanRetentions]
      const total = getConceptTotal(
        safeQuantity,
        safePrice,
        combinedTaxes,
        safeDiscount,
      )
      const conceptObj = {
        position: idx,
        description,
        quantity: safeQuantity,
        price: safePrice,
        discount: safeDiscount,
        taxes: cleanTaxes,
        retentions: cleanRetentions,
        subtotal,
        total,
      }
      if (cleanCertificationParts.length > 0) {
        conceptObj.certsAssociated = cleanCertificationParts
      }
      if (SK) conceptObj.SK = SK
      if (partidaId) conceptObj.partidaId = partidaId
      return conceptObj
    })

  const adapted = {
    ...invoice,
    company,
    invoiceConcepts: concepts,
    discount: Number.isFinite(Number(invoice.discount))
      ? Number(invoice.discount)
      : 0,
    // Normalised here rather than trusting the field's blur, which does not fire when
    // the form is submitted with Enter or while the input still has focus.
    iban: normalizeIban(invoice.iban),
  }
  delete adapted.certificationParts
  delete adapted.certsAssociated

  // AEAT reason codes are only valid on rectificative invoices.
  if (!CORRECTIVE_TYPES.has(invoice.invoiceType)) {
    delete adapted.reason
    delete adapted.correctionReasonCode
  }

  const formattedIssueDate = formatDate(invoice.issueDate)
  if (formattedIssueDate) adapted.issueDate = formattedIssueDate

  const formattedDueDate = formatDate(invoice.dueDate)
  adapted.dueDate = formattedDueDate || null

  return { ...adapted, ...options }
}
/**
 * Calculates the subtotal for a single concept (quantity * price, rounded).
 * @param {number} quantity
 * @param {number} price
 * @returns {number}
 */
export function getConceptSubtotal(quantity, price) {
  return round2((quantity || 0) * (price || 0))
}

/**
 * Calculates the total for a single concept (base - discount + tax - retention, rounded).
 * @param {number} quantity
 * @param {number} price
 * @param {Array} taxes - Array containing tax and retention objects
 * @param {number} discount - Discount percentage (0-100)
 * @returns {number}
 */
export function getConceptTotal(quantity, price, taxes = [], discount = 0) {
  const base = (quantity || 0) * (price || 0)
  const discountAmount = base * ((discount || 0) / 100)
  const baseAfterDiscount = base - discountAmount
  let tax = 0
  let retention = 0
  if (Array.isArray(taxes)) {
    taxes.forEach(t => {
      const val = t && t.value ? t.value / 100 : 0
      if (t && t.type === "retention") {
        retention += baseAfterDiscount * val
      } else {
        // treat anything else as tax (including undefined type)
        tax += baseAfterDiscount * val
      }
    })
  }
  return round2(baseAfterDiscount + tax - retention)
}
/**
 * Rounds a number to two decimal places (avoiding floating point errors).
 * @param {number} value
 * @returns {number}
 */
export function round2(value) {
  return Math.round((value + Number.EPSILON) * 100) / 100
}

/**
 * Calculates the subtotal for all concepts (with discount applied, without taxes or retentions).
 * @param {Array} concepts - Array of invoice concepts.
 * @returns {number} Subtotal.
 */
export function getInvoiceSubtotal(concepts) {
  const subtotal = concepts.reduce((acc, c) => {
    const base = (c.quantity || 0) * (c.price || 0)
    const discountAmount = base * ((c.discount || 0) / 100)
    return acc + (base - discountAmount)
  }, 0)
  return round2(subtotal)
}

/**
 * Calculates the total sum of taxes for all concepts (after product and global discount).
 * @param {Array} concepts - Array of invoice concepts.
 * @param {number} globalDiscount - Global discount percentage (0-100).
 * @returns {number} Total taxes.
 */
export function getInvoiceImpuestos(concepts, globalDiscount = 0) {
  let impuestos = 0
  concepts.forEach(c => {
    const base = (c.quantity || 0) * (c.price || 0)
    const productDiscountAmount = base * ((c.discount || 0) / 100)
    const baseAfterProductDiscount = base - productDiscountAmount
    const globalDiscountAmount =
      baseAfterProductDiscount * ((globalDiscount || 0) / 100)
    const baseAfterAllDiscounts =
      baseAfterProductDiscount - globalDiscountAmount
    if (Array.isArray(c.taxes)) {
      const tax = c.taxes.find(t => t.type === "tax")
      if (tax) impuestos += baseAfterAllDiscounts * (tax.value / 100)
    }
  })
  return round2(impuestos)
}

/**
 * Calculates the total sum of retentions for all concepts (after product and global discount).
 * @param {Array} concepts - Array of invoice concepts.
 * @param {number} globalDiscount - Global discount percentage (0-100).
 * @returns {number} Total retentions.
 */
export function getInvoiceRetenciones(concepts, globalDiscount = 0) {
  let retenciones = 0
  concepts.forEach(c => {
    const base = (c.quantity || 0) * (c.price || 0)
    const productDiscountAmount = base * ((c.discount || 0) / 100)
    const baseAfterProductDiscount = base - productDiscountAmount
    const globalDiscountAmount =
      baseAfterProductDiscount * ((globalDiscount || 0) / 100)
    const baseAfterAllDiscounts =
      baseAfterProductDiscount - globalDiscountAmount
    if (Array.isArray(c.taxes)) {
      const retention = c.taxes.find(t => t.type === "retention")
      if (retention)
        retenciones += baseAfterAllDiscounts * (retention.value / 100)
    }
  })
  return round2(retenciones)
}

/**
 * Calculates the final invoice total (subtotal - globalDiscount + taxes - retentions).
 * @param {Array} concepts - Array of invoice concepts.
 * @param {number} globalDiscount - Global discount percentage (0-100).
 * @returns {number} Invoice total.
 */
export function getInvoiceTotal(concepts, globalDiscount = 0) {
  const subtotal = getInvoiceSubtotal(concepts)
  const globalDiscountAmount = subtotal * ((globalDiscount || 0) / 100)
  const subtotalAfterGlobalDiscount = subtotal - globalDiscountAmount
  const impuestos = getInvoiceImpuestos(concepts, globalDiscount)
  const retenciones = getInvoiceRetenciones(concepts, globalDiscount)
  return round2(subtotalAfterGlobalDiscount + impuestos - retenciones)
}

/**
 * Groups taxes by name and calculates the total amount for each tax type (after product and global discount).
 * @param {Array} concepts - Array of invoice concepts.
 * @param {number} globalDiscount - Global discount percentage (0-100).
 * @returns {Array} Array of { name, value (percentage), amount (calculated) }
 */
export function getGroupedTaxes(concepts, globalDiscount = 0) {
  const taxMap = {}
  concepts.forEach(c => {
    const base = (c.quantity || 0) * (c.price || 0)
    const productDiscountAmount = base * ((c.discount || 0) / 100)
    const baseAfterProductDiscount = base - productDiscountAmount
    const globalDiscountAmount =
      baseAfterProductDiscount * ((globalDiscount || 0) / 100)
    const baseAfterAllDiscounts =
      baseAfterProductDiscount - globalDiscountAmount
    if (Array.isArray(c.taxes)) {
      c.taxes.forEach(t => {
        if (t.type === "tax") {
          const key = t.name || t.longName || `Tax ${t.value}%`
          const amount = baseAfterAllDiscounts * ((t.value || 0) / 100)
          if (!taxMap[key]) {
            taxMap[key] = { name: key, value: t.value, amount: 0 }
          }
          taxMap[key].amount += amount
        }
      })
    }
  })
  // Convert to array and round amounts
  return Object.values(taxMap).map(t => ({
    ...t,
    amount: round2(t.amount),
  }))
}

/**
 * Groups retentions by name and calculates the total amount for each retention type (after product and global discount).
 * @param {Array} concepts - Array of invoice concepts.
 * @param {number} globalDiscount - Global discount percentage (0-100).
 * @returns {Array} Array of { name, value (percentage), amount (calculated) }
 */
export function getGroupedRetentions(concepts, globalDiscount = 0) {
  const retentionMap = {}
  concepts.forEach(c => {
    const base = (c.quantity || 0) * (c.price || 0)
    const productDiscountAmount = base * ((c.discount || 0) / 100)
    const baseAfterProductDiscount = base - productDiscountAmount
    const globalDiscountAmount =
      baseAfterProductDiscount * ((globalDiscount || 0) / 100)
    const baseAfterAllDiscounts =
      baseAfterProductDiscount - globalDiscountAmount
    if (Array.isArray(c.taxes)) {
      c.taxes.forEach(t => {
        if (t.type === "retention") {
          const key = t.name || t.longName || `Retention ${t.value}%`
          const amount = baseAfterAllDiscounts * ((t.value || 0) / 100)
          if (!retentionMap[key]) {
            retentionMap[key] = { name: key, value: t.value, amount: 0 }
          }
          retentionMap[key].amount += amount
        }
      })
    }
  })
  // Convert to array and round amounts
  return Object.values(retentionMap).map(r => ({
    ...r,
    amount: round2(r.amount),
  }))
}

/**
 * Builds the invoice edit URL
 * @param {string} SK - Invoice SK (format: INVOICE#id)
 * @param {string} projectId - Optional project ID
 * @returns {string} Invoice edit URL
 */
export function getInvoiceEditUrl(invoiceId, projectId) {
  if (!invoiceId) return null
  let url = `/invoice/${invoiceId}`
  if (projectId) {
    url += `/${projectId}`
  }
  return url
}

/**
 * Compares two rows by a date field for sorting purposes.
 * @param {Object} row1 - First row object
 * @param {Object} row2 - Second row object
 * @param {string} dateField - The field name containing the date (e.g., 'issueDate', 'dueDate')
 * @returns {number} - Negative if row1 < row2, positive if row1 > row2, 0 if equal
 */
export function compareDates(row1, row2, dateField) {
  const date1 = row1[dateField] ? new Date(row1[dateField]).getTime() : 0
  const date2 = row2[dateField] ? new Date(row2[dateField]).getTime() : 0
  return date1 - date2
}

/**
 * Returns the retention rate value from a concept, or null if the concept has no retention.
 * Checks both `taxes` (items with type==="retention") and `retentions` arrays.
 * @param {Object} concept
 * @returns {number|null}
 */
function getConceptRetentionRate(concept) {
  const fromTaxes = Array.isArray(concept.taxes)
    ? concept.taxes.find(t => t.type === "retention")
    : null
  const fromRetentions = Array.isArray(concept.retentions)
    ? concept.retentions[0]
    : null
  const retention = fromTaxes || fromRetentions
  return retention ? (retention.value ?? null) : null
}

/**
 * Validates that all concepts with retentions share the same retention rate.
 * Invopop only supports a single retention rate across all concepts.
 * Returns an i18n key if inconsistent, null if valid.
 * @param {Array} concepts
 * @returns {string|null}
 */
export function validateRetentionConsistency(concepts) {
  if (!Array.isArray(concepts) || concepts.length === 0) return null
  const rates = concepts.map(getConceptRetentionRate)
  const uniqueRates = new Set(rates)
  // All null (no retentions) or all same value → valid
  if (uniqueRates.size <= 1) return null
  return "invoices.inconsistentRetentions"
}

const DIRECT_DEBIT_CHECKS = [
  ["accountNumber", isValidIban],
  ["mandateRef", isValidMandateRef],
  ["creditorId", isValidCreditorId],
]

/**
 * The customer's SEPA mandate, checked with the same validators the company form uses.
 *
 * Mirrors the backend's `_assert_direct_debit_data`, including its missing-before-invalid
 * order, and returns the same two codes so both paths produce one message. Catching it
 * here only saves the round trip: the number is spent on the way out, so the backend
 * still has to block.
 *
 * @param {Object|null} contability - the company's accounting record
 * @returns {string|null} i18n key, or null when the mandate is complete and valid
 */
function validateDirectDebitData(contability) {
  const data = contability || {}

  const missing = DIRECT_DEBIT_CHECKS.some(
    ([field]) => !String(data[field] || "").trim(),
  )
  if (missing) return "invoices.issueReasons.DIRECT_DEBIT_DATA_MISSING"

  const invalid = DIRECT_DEBIT_CHECKS.some(
    ([field, isValid]) => !isValid(data[field]),
  )
  if (invalid) return "invoices.issueReasons.DIRECT_DEBIT_DATA_INVALID"

  return null
}

export function validateInvoiceForm(
  invoiceData,
  invalidContact,
  activeInvoiceSeries,
  selectedSerieSK,
  { isIssuing = false, contability = null } = {},
) {
  if (invalidContact) return "invoices.selectClient"

  const isEmpty = v => !v || (typeof v === "string" && v.trim() === "")
  const hasErrors =
    [
      invoiceData.issueDate,
      invoiceData.contact,
      invoiceData.paymentMethod,
    ].some(isEmpty) ||
    (invoiceData.invoiceConcepts || []).some(c => isEmpty(c.description))

  if (hasErrors) return "invoices.missingFields"

  // Only a transfer declares the account, so only there does a bad one misdirect
  // money. Mirrors the backend's `_assert_transfer_iban`, which raises INVALID_IBAN.
  if (
    invoiceData.paymentMethod === "transfer" &&
    invoiceData.iban &&
    !isValidIban(invoiceData.iban)
  )
    return "invoices.ibanInvalid"

  // The mandate lives on the company, not the invoice, so the payment method alone
  // decides whether it is required -- a pending company fetch must not wave it through.
  // Only on issue, though: a draft is still being filled in, and the number is not
  // spent yet, so blocking a save would strand work in progress.
  if (isIssuing && invoiceData.paymentMethod === "directDebit") {
    const directDebitError = validateDirectDebitData(contability)
    if (directDebitError) return directDebitError
  }

  if (!activeInvoiceSeries || activeInvoiceSeries.length === 0)
    return "invoices.noActiveSerie"

  if (!selectedSerieSK) return "invoices.selectSerie"

  if (CORRECTIVE_TYPES.has(invoiceData.invoiceType)) {
    const reason = (invoiceData.reason ?? "").trim()
    if (reason.length <= 5) return "invoices.reasonTooShort"
  }

  if (isIssuing) {
    const retentionError = validateRetentionConsistency(
      invoiceData.invoiceConcepts,
    )
    if (retentionError) return retentionError
  }

  return null
}
